import type { MeasurementPlane, PlaneVec2, PlaneVec3 } from '../../store/reducers/measurementPlanes.reducer.ts';
import { estimateNormalsKnn } from '../measurementCalculation/normals.ts';
import { type OcclusionScene, closestHit } from '../measurementCalculation/occlusion.ts';
import type { CalcPoint, Vec3 } from '../measurementCalculation/types.ts';
import { frameToWorld } from './frame.ts';

/** Lift (m) of a measurement point above the surface it sits on — matches taman-ml's
 *  grid lift (grid.ts EPSILON_RAYCASTING) so surface points sit exactly like the
 *  whole-model "All model points". */
export const POINT_LIFT_M = 0.01;

/**
 * DRAPE a HORIZONTAL plane's (u,v) grid straight down onto the model (like taman-ml's
 * grid drop): keep each hit at `z + 0.01`, with KNN-PCA normals over the cloud — identical
 * to how the whole-model "All model points" are positioned, so the values match.
 * Coordinates with no surface beneath them are dropped (the footprint can overhang).
 */
export function drapePlaneGrid(scene: OcclusionScene, grid: PlaneVec2[]): CalcPoint[] {
  const topZ = scene.nodeMax[2] + 1;
  const maxT = topZ - scene.nodeMin[2] + 2;
  const positions: Vec3[] = [];
  for (const [u, v] of grid) {
    const hit = closestHit(scene, u, v, topZ, 0, 0, -1, maxT);
    if (!hit) continue;
    positions.push([u, v, topZ - hit.t + POINT_LIFT_M]);
  }
  const normals = estimateNormalsKnn(positions);
  return positions.map((position, i) => ({ position, normal: normals[i] }));
}

/**
 * DRAPE an oriented (façade) plane's (u,v) grid by raycasting FROM the camera position the
 * user drew from, through each grid point, onto the model. The point lands on the exact
 * surface the user was looking at — so it follows protrusions/recesses instead of a flat
 * plane, and it's never placed inside the building. The hit normal is oriented toward the
 * camera and the point lifted `0.01 m` along it. Coordinates whose ray misses are dropped.
 */
export function drapeFromCamera(
  scene: OcclusionScene,
  plane: MeasurementPlane,
  grid: PlaneVec2[],
  cameraPos: PlaneVec3,
): CalcPoint[] {
  const f = plane.frame;
  if (!f) return [];
  const points: CalcPoint[] = [];
  const MAX_T = 1e6;
  for (const [u, v] of grid) {
    const w = frameToWorld([u, v], f); // target on the drawn plane
    let dx = w[0] - cameraPos[0];
    let dy = w[1] - cameraPos[1];
    let dz = w[2] - cameraPos[2];
    const len = Math.hypot(dx, dy, dz) || 1;
    dx /= len;
    dy /= len;
    dz /= len;
    const hit = closestHit(scene, cameraPos[0], cameraPos[1], cameraPos[2], dx, dy, dz, MAX_T);
    if (!hit) continue;
    const hx = cameraPos[0] + dx * hit.t;
    const hy = cameraPos[1] + dy * hit.t;
    const hz = cameraPos[2] + dz * hit.t;
    // Orient the hit normal toward the camera (lift the point toward the viewer/lit side).
    let nx = hit.nx;
    let ny = hit.ny;
    let nz = hit.nz;
    if (nx * dx + ny * dy + nz * dz > 0) {
      nx = -nx;
      ny = -ny;
      nz = -nz;
    }
    points.push({
      position: [hx + nx * POINT_LIFT_M, hy + ny * POINT_LIFT_M, hz + nz * POINT_LIFT_M],
      normal: [nx, ny, nz],
    });
  }
  return points;
}
